/**
 * A general-purpose hook for fetching AR Collection Queue data.
 *
 * This hook wraps React Query's `useQuery` to provide a unified way
 * to request and cache collection-queue results across the application.
 *
 **/
import { useQuery, useMutation } from '@tanstack/react-query';
import { arCollectionQueueApi } from '@xtnd/web/features/ar-collections/api/arCollectionsQueueApi';
import type { ArCollectionQueueParams } from '../types';

export function useCollectionQueue(
  params: ArCollectionQueueParams,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ['collectionQueue', params] as const,
    queryFn: () => arCollectionQueueApi.getCollectionQueue(params),
    enabled: options?.enabled ?? true,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

export function useCollectionQueueExport(params: ArCollectionQueueParams) {
  return useQuery({
    queryKey: ['exportCollectionQueue', params] as const,
    queryFn: () => arCollectionQueueApi.exportCollection(params),
    enabled: false,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 0,
  });
}

export function useCollectionQueuePdfExport() {
  return useMutation({
    mutationFn: (params: ArCollectionQueueParams) =>
      arCollectionQueueApi.exportCollectionPdf(params),
  });
}

export const usePreviewFileQuery = (
  key: string,
  options: { enabled?: boolean } = {},
  category?: string
) => {
  return useQuery({
    queryKey: ['preview-file', key, category] as const,
    queryFn: () => arCollectionQueueApi.getPreviewFile(key, category),
    enabled: !!key && (options.enabled ?? true),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
};
